import apiClient from '../api/client';
import { downloadAuthenticatedFile } from '../utils/authenticatedDownload';

// ── Types ────────────────────────────────────────────────────────

export interface AssayType {
  id: number;
  name: string;
  category: string;
  description: string | null;
  typical_units: string | null;
  input_mode: string | null;
  is_active?: boolean;
  internal_code?: string | null;
  target_name?: string | null;
  protocol_count?: number;
  run_count?: number;
  compound_count?: number;
}

export interface AssayTypeInput {
  name: string;
  category: string;
  description?: string | null;
  typical_units?: string | null;
  input_mode: string;
  is_active?: boolean;
  internal_code?: string | null;
  target_name?: string | null;
}

export interface ProtocolVersion {
  id: number;
  version_label: string;
  protocol_text?: string | null;
  document_filename?: string | null;
  document_size?: number | null;
  change_note?: string | null;
  is_current: boolean;
  created_at: string;
  created_by_name?: string | null;
  structured_definition?: Record<string, any>;
  extraction_status?: string;
  extraction_warnings?: string[];
}

export interface AssayRegistryItem extends AssayType {
  current_protocol_id?: number | null;
  current_protocol_version?: string | null;
  run_count: number;
  compound_count: number;
  completed_count: number;
  pending_count: number;
}

export interface AssayRun {
  id: number;
  run_code: string;
  run_date: string;
  status: string;
  operator_name?: string | null;
  notes?: string | null;
  protocol_version?: string | null;
  compound_count: number;
  completed_count: number;
  unplanned_count: number;
}

export interface EchoImportEvent {
  id: number; source_row: number; transfer_id?: string; source_plate_barcode?: string;
  source_well?: string; destination_well?: string; transferred_volume_nl?: number;
  sample_id?: string; compound_id?: string; batch_id?: string; sample_role?: string;
  stock_concentration_um?: number; calculated_concentration_um?: number;
  severity: 'INFO' | 'WARNING' | 'BLOCKER'; decision: 'INCLUDE' | 'EXCLUDE' | 'BLOCKED';
  issue_code?: string; issue_message?: string; exclusion_reason?: string;
}

export interface EchoImportBatch {
  id: number; assay_run_id: number; run_code: string; destination_plate_barcode: string;
  plate_format: number; final_well_volume_ul: number; filename: string; file_sha256: string;
  raw_document_id?: number; status: string; created_at: string; committed_at?: string;
  duplicate?: boolean; well_count?: number;
  summary: { parsed: number; included: number; excluded: number; blocked: number; destination_wells: number };
  events: EchoImportEvent[];
}

export interface EchoLineageTransfer {
  id: number; source_row: number; transfer_id?: string; source_plate_barcode?: string;
  source_well?: string; destination_plate_barcode?: string; destination_well?: string;
  requested_volume_nl?: number; transferred_volume_nl?: number; transfer_status?: string;
  sample_id?: string; compound_id?: string; batch_id?: string; sample_role?: string;
  stock_concentration_um?: number; calculated_concentration_um?: number; decision: string;
}

export interface EchoLineageWell {
  id: number; destination_plate_barcode: string; destination_well: string;
  primary_sample_id?: string; compound_id?: string; batch_id?: string; molecule_id?: number;
  compound_code?: string; sample_role?: string; replicate_number?: number;
  concentration_point?: number; include_in_curve_fit: boolean; stock_concentration_um?: number;
  compound_transfer_volume_nl?: number; final_well_volume_ul?: number;
  final_concentration_um?: number; transfer_event_count: number; layout_notes?: string;
  import_batch_id: number; import_filename: string; raw_document_id?: number;
  imported_at: string; committed_at?: string; result_status?: string; result_value?: number;
  result_text?: string; result_unit?: string; qualifier?: string; result_operator?: string;
  result_completed_at?: string; result_notes?: string; transfers: EchoLineageTransfer[];
}

export interface EchoRunLineage {
  run: { id: number; run_code: string; run_date: string; status: string; operator_name?: string;
    notes?: string; assay_type_id: number; assay_name: string; assay_code?: string;
    protocol_version_id?: number; protocol_version?: string };
  state: 'committed' | 'not_imported';
  selected_import_batch_id?: number;
  imports: Array<{ id: number; filename: string; destination_plate_barcode: string;
    plate_format: number; committed_at?: string }>;
  wells: EchoLineageWell[];
  well_count: number;
}

export interface EchoDoseResponsePoint {
  concentration_um: number; mean_response: number; sd_response: number;
  replicate_count: number; fitted_response?: number;
}

export interface EchoReaderMeasurement {
  plate: string; well: string; concentration_um?: number; concentration_source?: string;
  replicate?: number; sample_role?: string; read_id?: string; raw_rfu?: number;
  background_subtracted_rfu?: number; percent_inhibition?: number; result_status?: string;
}

export interface EchoDoseResponseResult {
  id: number; molecule_id: number; compound_id: string; status: string; ic50_um?: number;
  pic50?: number; hill_slope?: number; top?: number; bottom?: number; r_squared?: number;
  rmse?: number; concentration_count: number; replicate_count: number; fit_error?: string;
  processed_at: string; import_filename: string; raw_document_id?: number;
  import_batch_id: number; destination_plate_barcode?: string;
  points: EchoDoseResponsePoint[]; measurements: EchoReaderMeasurement[];
}

export interface EchoRunResults {
  run: { id: number; run_code: string; status: string; operator_name?: string; run_date: string };
  results: EchoDoseResponseResult[];
  selected_import_batch_id?: number;
  imports: Array<{ id: number; filename: string; destination_plate_barcode: string;
    plate_format: number; committed_at?: string }>;
  controls: Array<{ sample_role: string; count: number; mean_raw_rfu?: number;
    mean_background_subtracted_rfu?: number; mean_percent_inhibition?: number }>;
  plates: Array<{
    import_batch_id: number; barcode: string; format: number;
    wells: Array<{
      well: string; sample_id?: string; compound_id?: string; molecule_id?: number;
      sample_role?: string; replicate?: number; final_concentration_um?: number;
      raw_rfu?: number; background_subtracted_rfu?: number;
      percent_inhibition?: number; result_status?: string;
    }>;
  }>;
}

export interface AssayTypesResponse {
  types: AssayType[];
  by_category: Record<string, AssayType[]>;
  count: number;
}

export interface AssayRequest {
  id: number;
  project_id: number;
  molecule_id: number;
  batch_id: number | null;
  assay_type_id: number;
  assay_type_name: string;
  assay_category: string;
  input_mode: string;
  typical_units: string | null;
  target_name: string | null;
  submitted_by: string | null;
  submitted_by_name?: string;
  assigned_to: string | null;
  assigned_to_name?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  notes: string | null;
  result_value: number | null;
  result_unit: string | null;
  result_binary: boolean | null;
  result_hill_slope: number | null;
  result_r_squared: number | null;
  result_top: number | null;
  result_bottom: number | null;
  curve_image_path?: boolean | string;
  has_curve_image?: boolean;
  curve_image_url?: string | null;
  reject_reason?: string | null;
  compound_code?: string;
  smiles?: string;
  completed_at: string | null;
  created_at: string | null;
  data_points?: AssayDataPoint[];
}

export interface AssayDataPoint {
  id: number;
  concentration: number;
  concentration_unit: string;
  response: number;
  response_unit: string;
  replicate: number;
  well_position: string | null;
  plate_id: string | null;
  created_at: string | null;
}

export interface AssayRequestCreate {
  molecule_id: number;
  project_id: number;
  assay_type_id: number;
  batch_id?: number;
  target_name?: string;
  notes?: string;
}

// ── API Functions ────────────────────────────────────────────────

export const assayRequestApi = {
  fetchAssayTypes: async (): Promise<AssayTypesResponse> => {
    const res = await apiClient.get('/assay-types/');
    return res.data;
  },

  fetchAdminAssayTypes: async (): Promise<{ types: AssayType[]; count: number }> => {
    const res = await apiClient.get('/admin/assay-types/');
    return res.data;
  },

  createAssayType: async (data: AssayTypeInput): Promise<AssayType> => {
    const res = await apiClient.post('/admin/assay-types/', data);
    return res.data;
  },

  updateAssayType: async (id: number, data: AssayTypeInput): Promise<AssayType> => {
    const res = await apiClient.put(`/admin/assay-types/${id}`, data);
    return res.data;
  },

  uploadProtocolVersion: async (assayTypeId: number, data: {
    version_label: string; protocol_text?: string; change_note?: string; document?: File | null;
    structured_definition?: Record<string, any> | null;
    extraction_warnings?: string[];
  }): Promise<ProtocolVersion> => {
    const form = new FormData();
    form.append('version_label', data.version_label);
    if (data.protocol_text) form.append('protocol_text', data.protocol_text);
    if (data.change_note) form.append('change_note', data.change_note);
    if (data.document) form.append('document', data.document);
    if (data.structured_definition) form.append('structured_definition', JSON.stringify(data.structured_definition));
    if (data.extraction_warnings) form.append('extraction_warnings', JSON.stringify(data.extraction_warnings));
    const res = await apiClient.post(`/admin/assay-types/${assayTypeId}/protocol-versions`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  extractProtocolDocument: async (assayTypeId: number, document: File): Promise<{
    filename: string; definition: Record<string, any>; warnings: string[]; paragraph_count: number; table_count: number;
  }> => {
    const form = new FormData();
    form.append('document', document);
    const res = await apiClient.post(`/admin/assay-types/${assayTypeId}/extract-protocol`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  fetchProtocolVersions: async (assayTypeId: number): Promise<{ versions: ProtocolVersion[] }> => {
    const res = await apiClient.get(`/assay-types/${assayTypeId}/protocol-versions`);
    return res.data;
  },

  fetchAssayRegistry: async (): Promise<{ assays: AssayRegistryItem[]; count: number }> => {
    const res = await apiClient.get('/assay-registry/');
    return res.data;
  },

  fetchAssayRegistryDetail: async (id: number): Promise<any> => {
    const res = await apiClient.get(`/assay-registry/${id}`);
    return res.data;
  },

  createAssayRun: async (data: {
    assay_type_id: number; molecule_ids: number[]; project_id?: number;
    operator_name?: string; run_date?: string; notes?: string;
  }): Promise<any> => {
    const res = await apiClient.post('/assay-runs/', data);
    return res.data;
  },

  uploadRunResults: async (runId: number, file: File): Promise<any> => {
    const form = new FormData();
    form.append('results_file', file);
    const res = await apiClient.post(`/assay-runs/${runId}/results-upload`, form);
    return res.data;
  },

  createEchoImport: async (runId: number, data: {
    destination_plate_barcode?: string; plate_format?: number | null; final_well_volume_ul?: number | null;
    transfer_log?: File | null; source_lookup?: File | null; plate_layout?: File | null; workbook?: File | null;
  }): Promise<EchoImportBatch> => {
    const form = new FormData();
    if (data.destination_plate_barcode) form.append('destination_plate_barcode', data.destination_plate_barcode);
    if (data.plate_format != null) form.append('plate_format', String(data.plate_format));
    if (data.final_well_volume_ul != null) form.append('final_well_volume_ul', String(data.final_well_volume_ul));
    if (data.transfer_log) form.append('transfer_log', data.transfer_log);
    if (data.source_lookup) form.append('source_lookup', data.source_lookup);
    if (data.plate_layout) form.append('plate_layout', data.plate_layout);
    if (data.workbook) form.append('workbook', data.workbook);
    const res = await apiClient.post(`/echo-imports/run/${runId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  fetchEchoImports: async (runId: number): Promise<{ imports: EchoImportBatch[] }> => {
    const res = await apiClient.get(`/echo-imports/run/${runId}`);
    return res.data;
  },

  fetchEchoRunLineage: async (runId: number, moleculeId?: number, importBatchId?: number): Promise<EchoRunLineage> => {
    const res = await apiClient.get(`/echo-imports/run/${runId}/lineage`, {
      params: {
        ...(moleculeId == null ? {} : { molecule_id: moleculeId }),
        ...(importBatchId == null ? {} : { import_batch_id: importBatchId }),
      },
    });
    return res.data;
  },

  fetchEchoRunResults: async (runId: number, moleculeId?: number, importBatchId?: number): Promise<EchoRunResults> => {
    const res = await apiClient.get(`/echo-imports/run/${runId}/results`, {
      params: {
        ...(moleculeId == null ? {} : { molecule_id: moleculeId }),
        ...(importBatchId == null ? {} : { import_batch_id: importBatchId }),
      },
    });
    return res.data;
  },

  setEchoEventExcluded: async (batchId: number, eventId: number, excluded: boolean, reason: string): Promise<EchoImportBatch> => {
    const res = await apiClient.patch(`/echo-imports/${batchId}/events/${eventId}`, { excluded, reason });
    return res.data;
  },

  commitEchoImport: async (batchId: number): Promise<EchoImportBatch> => {
    const res = await apiClient.post(`/echo-imports/${batchId}/commit`);
    return res.data;
  },

  downloadEchoImportReport: (batchId: number): Promise<string> =>
    downloadAuthenticatedFile(`/echo-imports/${batchId}/report.csv`, `echo-validation-${batchId}.csv`),

  downloadAssayResultsTemplate: (): Promise<string> =>
    downloadAuthenticatedFile('/assay-runs/template.csv', 'assay-results-template.csv'),

  downloadProtocolDocument: (assayTypeId: number, versionId: number): Promise<string> =>
    downloadAuthenticatedFile(
      `/assay-types/${assayTypeId}/protocol-versions/${versionId}/download`,
      `assay-protocol-${assayTypeId}-v${versionId}.docx`,
    ),

  fetchMoleculeAssayHistory: async (moleculeId: number): Promise<any> => {
    const res = await apiClient.get(`/molecules/${moleculeId}/assay-history`);
    return res.data;
  },

  submitAssayRequest: async (data: AssayRequestCreate): Promise<AssayRequest> => {
    const res = await apiClient.post('/assay-requests/', data);
    return res.data;
  },

  fetchAssayRequests: async (filters: {
    status?: string;
    molecule_id?: number;
    project_id?: number;
    assigned_to?: string;
  }): Promise<{ requests: AssayRequest[]; count: number }> => {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status = filters.status;
    if (filters.molecule_id) params.molecule_id = filters.molecule_id;
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.assigned_to) params.assigned_to = filters.assigned_to;
    const res = await apiClient.get('/assay-requests/', { params });
    return res.data;
  },

  fetchAssayRequest: async (id: number): Promise<AssayRequest> => {
    const res = await apiClient.get(`/assay-requests/${id}`);
    return res.data;
  },

  getCurveImageUrl: (id: number): string => {
    return `/api/assay-requests/${id}/curve-image`;
  },

  /** Claim / pick up a pending request (biologist) */
  claimAssayRequest: async (id: number): Promise<AssayRequest> => {
    const res = await apiClient.patch(`/assay-requests/${id}/claim`);
    return res.data;
  },

  /** Submit dose-response data points (paste or structured) */
  submitDoseResponseData: async (id: number, data: {
    points?: Array<{ concentration: number; response: number; replicate?: number; well_position?: string; plate_id?: string }>;
    raw_text?: string;
    concentration_unit?: string;
    response_unit?: string;
  }): Promise<{ request_id: number; points_saved: number; data: AssayDataPoint[]; concentration_unit: string; response_unit: string }> => {
    const res = await apiClient.post(`/assay-requests/${id}/data`, data);
    return res.data;
  },

  /** Submit a single value or binary result */
  submitSingleValueResult: async (id: number, data: {
    value?: number;
    unit?: string;
    binary_result?: boolean;
    notes?: string;
  }): Promise<{ request_id: number; input_mode: string; result_value: number | null; result_unit: string | null; result_binary: boolean | null }> => {
    const res = await apiClient.post(`/assay-requests/${id}/result`, data);
    return res.data;
  },

  /** Run 4PL curve fitting on submitted data */
  calculateCurveFit: async (id: number): Promise<{
    request_id: number;
    fit: { ic50: number; hill_slope: number; top: number; bottom: number; r_squared: number; log_ic50: number; error?: string };
    curve_image_url: string;
    assay_type: string;
    target: string;
    unit: string;
  }> => {
    const res = await apiClient.post(`/assay-requests/${id}/calculate`);
    return res.data;
  },

  /** Approve request — send results back to chemist */
  approveAssayRequest: async (id: number): Promise<AssayRequest> => {
    const res = await apiClient.patch(`/assay-requests/${id}/approve`);
    return res.data;
  },

  /** Reject request with reason */
  rejectAssayRequest: async (id: number, reason: string): Promise<AssayRequest> => {
    const res = await apiClient.patch(`/assay-requests/${id}/reject`, { reason });
    return res.data;
  },
};
